import React, { useState } from 'react';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { CheckCircle2 } from 'lucide-react';
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
}
interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  position?: string;
}
export function ReferralForm() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.position.trim()) newErrors.position = 'Position is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIsSubmitting(false);
      setIsSuccess(true);
    }
  };
  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      position: ''
    });
    setIsSuccess(false);
  };
  if (isSuccess) {
    return <div className="text-center py-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-33">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-6 ring-8 ring-green-50" data-id="element-34">
          <CheckCircle2 className="h-8 w-8 text-green-700" data-id="element-35" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-36">
          Referral Submitted!
        </h3>
        <p className="text-gray-600 mb-8 max-w-sm mx-auto leading-relaxed" data-id="element-37">
          Thank you for referring{' '}
          <span className="font-semibold text-gray-900" data-id="element-38">
            {formData.firstName} {formData.lastName}
          </span>
          . HR will review the details and reach out shortly.
        </p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto" data-id="element-39">
          Submit Another Referral
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in duration-500" data-id="element-40">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-41">
        <Input label="First Name" name="firstName" placeholder="Jane" value={formData.firstName} onChange={handleChange} error={errors.firstName} disabled={isSubmitting} data-id="element-42" />
        <Input label="Last Name" name="lastName" placeholder="Doe" value={formData.lastName} onChange={handleChange} error={errors.lastName} disabled={isSubmitting} data-id="element-43" />
      </div>

      <Input label="Email Address" name="email" type="email" placeholder="jane.doe@example.com" value={formData.email} onChange={handleChange} error={errors.email} disabled={isSubmitting} data-id="element-44" />

      <Input label="Phone Number" name="phone" type="tel" placeholder="(555) 123-4567" value={formData.phone} onChange={handleChange} error={errors.phone} disabled={isSubmitting} data-id="element-45" />

      <div className="w-full" data-id="element-46">
        <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-47">
          Position
        </label>
        <select id="position" name="position" value={formData.position} onChange={handleChange} disabled={isSubmitting} className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600 transition-colors bg-white appearance-none ${errors.position ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'} ${!formData.position ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-48">
          <option value="" disabled data-id="element-49">
            Select a position
          </option>
          <option value="Loan Officer" data-id="element-50">Loan Officer</option>
          <option value="Processor" data-id="element-51">Processor</option>
          <option value="Team Lead" data-id="element-52">Team Lead</option>
          <option value="HR" data-id="element-53">HR</option>
        </select>
        {errors.position && <p className="mt-1.5 text-sm text-red-600 font-medium" data-id="element-54">
            {errors.position}
          </p>}
      </div>

      <div className="pt-4 border-t border-gray-100" data-id="element-55">
        <Button type="submit" className="w-full text-base py-3" isLoading={isSubmitting} data-id="element-56">
          Submit Referral
        </Button>
      </div>
    </form>;
}